import React, { useCallback, useEffect, useRef } from 'react'
import { AppState, Platform, StyleSheet, View } from 'react-native'
import * as Sentry from '@sentry/react-native'

import * as StoreReview from '../modules/atlas-store-review'
import { getRemoteConfigValue, refetchRemoteConfig, remoteConfigReady } from './Firebase/firebaseFirestore'
import {
  APP_REVIEW_TRIGGERS,
  isActivityRecordDue,
  maybeRequestAppReview,
  recordAppSession,
  touchAppSession,
} from '../utils/appReviewPrompt'

export { APP_REVIEW_TRIGGERS }

/**
 * ASO-RATING-2026-001 — the app-side wiring for the App Store rating request.
 *
 * Lifecycle, platform and dependency injection only. Every rule about WHEN to ask lives in
 * `utils/appReviewPrompt.js`; see its header for the full contract.
 */

const onError = (error) => {
  console.warn('[appReview] review request step failed; app continues', error)
  Sentry.captureException(error)
}

/**
 * Call at a completed core action. Never throws and never rejects, so a call site can fire it with
 * `void` after its own success path without being able to break it.
 *
 * @param {string} trigger — one of `APP_REVIEW_TRIGGERS`
 * @param {object | null | undefined} postHog — the `usePostHog()` client of the calling screen
 */
export function requestAppReviewAtMilestone(trigger, postHog) {
  return maybeRequestAppReview({
    trigger,
    platform: Platform.OS,
    nativeReview: StoreReview,
    getRemoteConfigValue,
    remoteConfigReady,
    refreshRemoteConfig: refetchRemoteConfig,
    postHog,
    onError,
  }).catch((error) => {
    // `maybeRequestAppReview` already resolves on every path; this is the belt for a defect in it.
    onError(error)
    return { requested: false, reason: 'error' }
  })
}

/**
 * Feeds the "never in the very first session" rule: tells the session record when the app became
 * active and when a person is actually using it.
 *
 * Mounted in Routes.js around the AppStack/AuthStack branch, below the boot spinner: a launch that
 * never reached a usable screen is not a session anyone could have formed an opinion in.
 *
 *   · mount, every return to `active`, and a touch anywhere below it → `recordAppSession`, which
 *     starts a new session only after `SESSION_INACTIVITY_GAP_MS` without activity (so a JS reload or
 *     crash-relaunch mid-use is not one, and the first touch on a till left untouched is);
 *   · `background` → `touchAppSession`, so the gap is measured from when the person left.
 *
 * TOUCHES, NOT A TIMER. `App.js` keeps the screen awake, so a till never backgrounds; a heartbeat
 * that recorded foreground time would keep it in its first session for as long as it stays on.
 * `onTouchStartCapture` on the wrapping View sees every touch in the tree before any child handles
 * it and claims nothing (the same capture-phase hook `PostHogProvider` uses for autocapture), and
 * the throttle keeps it to one storage write per `SESSION_ACTIVITY_THROTTLE_MS`.
 *
 * Deliberately not gated on sign-in or on the platform. It writes one small local record and sends
 * nothing anywhere, and counting from the first launch is what makes the rule true for a merchant
 * who installs, signs up, and trades in the same first session.
 */
export function AppReviewSessionBridge({ children }) {
  // When this process last recorded activity. Only a throttle: the persisted record decides
  // sessions, so a reload that resets it costs one extra write, never a wrong count.
  const lastRecordedAt = useRef(null)

  const recordActivation = useCallback(() => {
    lastRecordedAt.current = Date.now()
    void recordAppSession()
  }, [])

  const onTouchStartCapture = useCallback(() => {
    if (isActivityRecordDue(lastRecordedAt.current, Date.now())) recordActivation()
  }, [recordActivation])

  useEffect(() => {
    recordActivation()

    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') recordActivation()
      else if (nextState === 'background') void touchAppSession()
    })

    return () => {
      subscription?.remove?.()
    }
  }, [recordActivation])

  return (
    <View style={styles.fill} onTouchStartCapture={onTouchStartCapture}>
      {children}
    </View>
  )
}

const styles = StyleSheet.create({
  fill: { flex: 1 },
})
